<template>
  <div>

    <!-- Main Form -->
    <b-row>
      <b-col cols="12">
        <h3
          class="pt-3 d-flex justify-content-between align-items-center"
        >
          <strong>Message Delivery</strong>
          <!-- b-button variant="primary-outline">Delivery Report</b-button -->
        </h3>
        <p v-if="Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length">Your message will be delivered either as a direct message to the participants of the campaign or as a channe/group message to a selected channel/group, at the time you specify in the “send at” section.</p>
      </b-col>
      <b-col v-if="!isScheduled" cols="12">
        <b-alert show dismissible>Once you set your message status to 'Ready', you will only be able to update it until 5 minutes prior to its scheduled send time.</b-alert>
      </b-col>
      <b-col v-if="isScheduled" cols="12">
        <b-alert show>You cannot edit this message, as it is either delivered or in progress.</b-alert>
      </b-col>
      <b-col cols="12">
        <b-alert
          v-if="Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length"
          variant="info"
          show
        >
          This Slack message will be delivered on behalf of you ({{ syncedEmailAddresses[0].emailAddress }}).
        </b-alert>
        <b-alert
          v-else
          slot="from-before"
          variant="warning"
          show
        >
          It seems like no accounts have been connected to Real Links yet. In order for us to send Slack messages on your behalf, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Slack</a> page.
        </b-alert>
        <advanced-form
          autocomplete="off"
          :hideFormControl="true"
          :form="formFields"
          v-bind="form"
          v-model="form"
        />
      </b-col>
    </b-row>

    <!-- Input Form with preview -->
    <b-row>
      <b-col cols="12">
        <h3
          class="pt-3"
        >
          <strong>Message Content</strong>
        </h3>
      </b-col>
      <b-col cols="12" lg="7">
        <div class="mb-3">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview.</div>
        <custom-preview
          v-if="templateID === 'slack_custom'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <promote-content-preview
          v-if="templateID === 'slack_promote_a_job' || templateID === 'slack_promote_content'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <promote-multiple-content-preview
          v-if="templateID === 'slack_promote_jobs' || templateID === 'slack_promote_content_shares'"
          class="mb-3"
          :fromEmail="form.from"
          :jobs="inputForm.jobs || null"
          :contentShares="inputForm.contentShares || null"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <schedule-multiple-content-preview
          v-if="templateID === 'slack_schedule_jobs' || templateID === 'slack_schedule_content_shares'"
          class="mb-3"
          :fromEmail="form.from"
          :jobs="inputForm.jobs || null"
          :contentShares="inputForm.contentShares || null"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <invite-preview
          v-if="templateID === 'slack_invite'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <leaderboard-preview
          v-if="templateID === 'slack_leaderboard_individual' || templateID === 'slack_leaderboard_group'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :isIndividual="templateID === 'slack_leaderboard_individual'"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <collective-goal-preview
          v-if="templateID === 'slack_collective_goal'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :collectiveGoal="collectiveGoal"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
        <gamification-preview
          v-if="templateID === 'slack_gamification_individual'"
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :collectiveGoal="collectiveGoal"
          :inputs="inputForm"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
      </b-col>
      <b-col cols="12" lg="5">
        <advanced-form
          autocomplete="off"
          :hideFormControl="true"
          :form="inputFormFields"
          v-bind="inputForm"
          v-model="inputForm"
        />
      </b-col>
    </b-row>

    <!-- Variables that can be used -->
    <b-row>
      <b-col>
        <b-card no-body class="mb-1">
          <b-card-header class="d-flex justify-content-between align-items-center bg-white">
            <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
              <strong>Template variables you can use</strong>
            </div>
            <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
              <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
              <font-awesome-icon v-else icon="chevron-down" size="sm"/>
            </b-button>
          </b-card-header>
          <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
            <b>User related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Job related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Leaderboard related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="pt-3">
        <b-button
          variant="primary"
          @click="input"
          class="float-right"
          :disabled="isScheduled"
        >
          Save
        </b-button>
      </b-col>
    </b-row>

    <!-- Test Messsage Form -->
    <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
      <b-row>
        <b-col>
          <b-form>
            <test-message-form
              :body="body"
              :from="form.from"
              :job="inputForm.job"
              :jobs="inputForm.jobs || null"
              :channelID="form.channel"
              :contentShare="inputForm.contentShare"
              :contentShares="inputForm.contentShares || null"
              :collectiveGoal="collectiveGoal"
              :isIndividual="isIndividual"
            />
          </b-form>
        </b-col>
      </b-row>
    </b-modal>

    <!-- ALERT MODALS -->
    <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
      <div v-if="isIndividual">
        <p>This Slack message will be sent as a direct message to everyone in the selected group.</p>
      </div>
      <div v-else>
        <p>This Slack message will be sent as a group message to everyone in the selected group.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import slackTemplates from '@src/views/campaign-admin/templates/slack';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import PromoteContentPreview from '@src/components/campaign-admin/message-previews/slack/promote-content';
import PromoteMultipleContentPreview from '@src/components/campaign-admin/message-previews/slack/promote-multiple-content';
import ScheduleMultipleContentPreview from '@src/components/campaign-admin/message-previews/slack/schedule-multiple-content';
import CustomPreview from '@src/components/campaign-admin/message-previews/slack/custom';
import InvitePreview from '@src/components/campaign-admin/message-previews/slack/invite';
import LeaderboardPreview from '@src/components/campaign-admin/message-previews/slack/leaderboard';
import CollectiveGoalPreview from '@src/components/campaign-admin/message-previews/slack/collectiveGoal';
import GamificationPreview from '@src/components/campaign-admin/message-previews/slack/gamification';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
// import slackifyHtml from 'slackify-html';
import TurndownService from 'turndown';
import gql from 'graphql-tag';

const SlackChannelsQuery = gql`
  query SlackChannelsQuery {
    user {
      id
      slackChannels {
       id
       title
       description
      }
    }
  }
`;

export default {
  props: {
    campaignID: {
      type: String,
    },
    collectiveGoal: {
      type: Object,
    },
    value: {
      type: Object,
    },
    syncedEmailAddresses: {
      type: Array,
    }
  },
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    PromoteContentPreview,
    CustomPreview,
    InvitePreview,
    LeaderboardPreview,
    CollectiveGoalPreview,
    PromoteMultipleContentPreview,
    ScheduleMultipleContentPreview,
    GamificationPreview,
  },
  apollo: {
    channels: {
      query: SlackChannelsQuery,
      update(data) {
        const { user } = data || {};
        const { slackChannels } = user || {};
        const channels = Array.isArray(slackChannels) ? slackChannels.reduce((r, { id, title, description }) => {
          if (!title || title.includes('mpdm')) return r;
          return {
            ...r,
            [id]: `Channel - ${title}${description ? ` - ${description}` : ''}`
          };
        }, {}) : {};
        const groups = Array.isArray(slackChannels) ? slackChannels.reduce((r, { id, title, description }) => {
          if (!title || !title.includes('mpdm')) return r;
          const parsedTitle = title.replace('mpdm-', 'Group - ').split('--').join(', ');
          return {
            ...r,
            [id]: `${parsedTitle}${description ? ` - ${description}` : ''}`
          };
        }, {}) : {};
        return { ...channels, ...groups };
      },
      skip() {
        const { form } = this;
        if (form && form.to === 'CHANNEL') {
          return false;
        }
        return true;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load Teams channels' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  data() {
    const { slackMessage } = this.value || {};
    const { syncedEmailAddresses } = this;
    delete slackMessage.__typename;
    const { template, inputs, body, job, contentShare, jobs, contentShares, ...rest } = slackMessage || {};
    return {
      form: {
        to: 'ALL_USERS',
        sendAt: '',
        status: 'DRAFT',
        ...(rest || {}),
        // important to set here - default value needs to be overwritten
        from: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length ? syncedEmailAddresses[0].emailAddress : null,
      },
      isScheduled: false,
      templateID: template || null,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          if (key.includes('IMAGE')) {
            try {
              acc[key] = JSON.parse(inputs[key]);
            } catch (e) {
              console.log(e);
              acc[key] = inputs[key];
            }
          } else {
            acc[key] = inputs[key];
          }
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {}),
        ...(jobs ? { jobs } : {}),
        ...(contentShares ? { contentShares } : {})
      },
      displayVariables: false,
    };
  },
  mounted() {
    setInterval(() => {
      const { form } = this;
      const isScheduled = [ 'SCHEDULED', 'SCHEDULING', 'ONHOLD', 'DELIVERED', 'ERRORED' ].includes(form.status);
      const now = new Date();
      const sendAt = typeof form.sendAt === 'string' || typeof form.sendAt === 'number' ? new Date(form.sendAt) : form.sendAt;
      const isLessThanFiveMinutes = sendAt && (sendAt.getTime() - now.getTime()) < 300000 && form.status === 'READY';
      this.isScheduled = isScheduled || isLessThanFiveMinutes;
    }, 1000);
  },
  mixins: [
    gridMixin,
  ],
  watch: {
    isIndividual() {
      this.$bvModal.show('target-group-change');
    }
  },
  computed: {
    template() {
      const { templateID, isPicnic } = this;
      return slackTemplates(isPicnic ? 'picnic' : 'default').find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual } = this;
      return {
        ALL_USERS: `All Participants: Everyone in the campaign - ${isIndividual ? 'sent as a direct Slack message' : 'posted in a group'}`,
        REGISTERED_USERS: `Active Participants: Those who have engaged with the campaign - ${isIndividual ? 'sent as a direct Slack message' : 'posted in a group'}`,
        INVITED_USERS: `Inactive Participants: Those who have not engaged with the campaign yet - ${isIndividual ? 'sent as a direct Slack message' : 'posted in a group'}`,
        CHANNEL: 'Channel: Sent as a channel message',
      };
    },
    formFields() {
      const { campaignID } = this;
      console.log({ campaignID });
      // const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        // from: {
        //   type: 'SELECT',
        //   label: 'From',
        //   // disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length || this.isScheduled,
        //   disabled: !!(Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length),
        //   choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
        //     ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
        //       out[emailAddress] = `${emailAddress} (${fullName})`;
        //       return out;
        //     }, {}) : [ defaultEmailOption ],
        //   helpText: 'The person this message will be delivered on behalf of'
        // },
        ...(Array.isArray(this.syncedEmailAddresses) && this.syncedEmailAddresses.length ? {
          to: {
            label: 'To',
            type: 'SELECT',
            required: true,
            choices: this.toChoices,
            disabled: this.isScheduled,
            helpText: 'The target group of this Slack message'
          },
        } : {}),
        ...(this.form.to === 'CHANNEL' && this.channels ? {
          channel: {
            label: 'Channel / Group',
            type: 'SELECT',
            required: true,
            choices: this.channels,
            helpText: 'The target group of this Slack message'
          },
        } : {}),
        sendAt: {
          label: 'Send At',
          placeholder: 'e.g. 2020-12-01 13:00',
          helpText: 'The time when this Slack message will be sent, in your timezone',
          disabled: this.isScheduled,
          type: 'DATETIME',
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          helpText: 'The status of this Slack message. The message will not be delivered until the status is set to "Ready"',
          disabled: this.isScheduled,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template } = this;
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the Slack message',
          },
        } : {}),
        ...(this.areJobs ? {
          jobs: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
              },
              many: true,
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 jobs you want to include in the Slack message',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the Slack message',
          },
        } : {}),
        ...(this.arePiecesOfContent ? {
          contentShares: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: true,
              where: {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 pieces of content you want to include in the Slack message',
          },
        } : {}),
        ...inputFields,
      };
    },
    body() {
      const { template, inputForm } = this;
      const turndownService = new TurndownService({ strongDelimiter: '*' });
      if (!template) return null;
      let { body } = template;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        Object.keys(inputForm).forEach(key => {
          if (typeof inputForm[key] === 'string') {
            replacedBody = replacedBody.replaceAll(`[[${key}]]`, turndownService
              .turndown(inputForm[key])
              .replaceAll('\\', '')
              .replaceAll('\n', '\\n')
            );
          }
        });
        return replacedBody;
      };
      const { jobs, contentShares } = inputForm;
      if (body && Array.isArray(body.blocks) && body.blocks.find(({ type }) => type === 'blocks') && template.blocks &&
        (Array.isArray(jobs) || Array.isArray(contentShares))) {
        const block = body.blocks.find(({ type }) => type === 'blocks');
        const blockIndex = body.blocks.findIndex(({ type }) => type === 'blocks');
        const { key: blockKey } = block;
        let items = [];
        if (blockKey === 'BLOCK_JOB') {
          const { jobs } = inputForm;
          items = jobs;
        }
        if (blockKey === 'BLOCK_CONTENT') {
          const { contentShares } = inputForm;
          items = contentShares;
        }
        console.log({ items });
        const blocks = items.map(({ id }) => JSON.parse(JSON.stringify(template.blocks[blockKey]).replaceAll('[ID]', id)));
        console.log({ blocks });

        // replace block in the body.blocks array with the new blocks
        body = {
          ...body,
          blocks: [
            ...body.blocks.slice(0, blockIndex),
            ...(blocks.reduce((r, block) => {
              if (Array.isArray(block)) r.push(...block);
              else r.push(block);
              return r;
            }, [])),
            ...body.blocks.slice(blockIndex + 1),
          ],
        };
        console.log({ body });
      }
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], tunrDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        if (typeof inputForm[key] === 'string') {
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, turndownService
            .turndown(inputForm[key])
            .replaceAll('\\', '')
            .replaceAll('\n', '\\n')
          );
        }
      });
      console.log({ replacedBody: JSON.parse(replacedBody) });
      return replacedBody;
    },
    usedVariables() {
      const { body } = this;
      const stringBody = typeof body === 'string' ? body : (body ? JSON.stringify(body) : null);
      return [ ...new Set((stringBody || '').split('{{').reduce((r, el) => {
        if (!el.includes('}}')) return r;
        const [ variable ] = el.split('}}');
        r.push(variable);
        return r;
      }, [])) ];
    },
    variables() {
      return [
        {
          key: 'JOB_TITLE',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Title of the selected job.',
        }, {
          key: 'JOB_SHARE_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'The link of the job users can share. This is generated uniquely for each user.',
        }, {
          key: 'JOB_DESCRIPTION',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected job.',
        }, {
          key: 'JOB_SHARE_TEXT',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the job.',
        }, {
          key: 'JOB_MATCH_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'LinkedIn network page of the user with possible matches',
        }, {
          key: 'CONTENT_TITLE',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Title of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_LINK',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'The link of the piece of content users can share. This is generated uniquely for each user.',
        }, {
          key: 'CONTENT_DESCRIPTION',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_TEXT',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the piece of content.',
        }, {
          key: 'LEADERBOARD_FIRST_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the first prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the second prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the third prize of the leaderboard.',
        }, {
          key: 'FIRST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The first name of the current user on the leaderboard.',
        }, {
          key: 'LAST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The last name of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POSITION',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The position of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POINTS',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The amount of points/tokens the current user has collected.',
        },
      ];
    },
    isIndividual() {
      const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      const { relations } = this.template || {};
      return !!keys.filter(key => this.usedVariables.includes(key)).length || (Array.isArray(relations) && (relations.includes('MANY_JOBS') || relations.includes('MANY_CONTENT')));
    },
    isJob() {
      const keys = this.variables.filter(({ type }) => type === 'JOB').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('JOB')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isContent() {
      const keys = this.variables.filter(({ type }) => type === 'CONTENT').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('CONTENT')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    areJobs() {
      const { relations } = this.template || {};
      return Array.isArray(relations) && relations.includes('MANY_JOBS');
    },
    arePiecesOfContent() {
      const { relations } = this.template || {};
      console.log({ relations });
      return Array.isArray(relations) && relations.includes('MANY_CONTENT');
    }
  },
  methods: {
    input() {
      const { form, inputForm } = this;
      if (inputForm.job) {
        delete inputForm.job.customFields;
        delete inputForm.job.location;
        delete inputForm.job.createdAt;
        delete inputForm.job.status;
        delete inputForm.job.department;
        delete inputForm.job.__typename;
      }
      if (Array.isArray(inputForm.jobs)) {
        for (let i = 0; i < inputForm.jobs.length; i++) {
          delete inputForm.jobs[i].customFields;
          delete inputForm.jobs[i].location;
          delete inputForm.jobs[i].createdAt;
          delete inputForm.jobs[i].status;
          delete inputForm.jobs[i].department;
          delete inputForm.jobs[i].__typename;
        }
      }
      if (inputForm.contentShare) {
        delete inputForm.contentShare.createdAt;
        delete inputForm.contentShare.status;
        delete inputForm.contentShare.atsID;
        delete inputForm.contentShare.url;
        delete inputForm.contentShare.domain;
        delete inputForm.contentShare.imageURL;
        delete inputForm.contentShare.image;
        delete inputForm.contentShare.__typename;
      }
      if (Array.isArray(inputForm.contentShares)) {
        for (let y = 0; y < inputForm.contentShares.length; y++) {
          delete inputForm.contentShares[y].createdAt;
          delete inputForm.contentShares[y].status;
          delete inputForm.contentShares[y].atsID;
          delete inputForm.contentShares[y].url;
          delete inputForm.contentShares[y].domain;
          delete inputForm.contentShares[y].imageURL;
          delete inputForm.contentShares[y].image;
          delete inputForm.contentShares[y].__typename;
        }
      }
      const { job, contentShare, jobs, contentShares, ...rest } = inputForm;
      this.$emit('input', {
        slackMessage: {
          template: this.templateID,
          ...form,
          ...(job ? { job } : {}),
          ...(contentShare ? { contentShare } : {}),
          ...(Array.isArray(jobs) ? { jobs } : {}),
          ...(Array.isArray(contentShares) ? { contentShares } : {}),
          target: this.isIndividual ? 'INDIVIDUAL' : 'GROUP',
          replacedBody: this.body,
          inputs: {
            ...(Object.keys(rest).reduce((acc, key) => {
              if (rest[key] && typeof rest[key] === 'object') {
                acc[key] = JSON.stringify(rest[key]);
              } else {
                acc[key] = rest[key];
              }
              return acc;
            }, {})),
          }
        } });
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-slack-message.vue